import {
  type ClaudeCodeEffort,
  type EnvironmentId,
  type MessageId,
  ProjectId,
  type ModelSelection,
  type ProviderKind,
  type ResolvedKeybindingsConfig,
  type ServerProvider,
  type ScopedThreadRef,
  type ThreadKind,
  type ThreadId,
  type TurnId,
} from "@cmmd-forge/contracts";
import { applyClaudePromptEffortPrefix } from "@cmmd-forge/shared/model";
import { AUTH_EXPIRED_REASON_KEY } from "../lib/authExpiredRedirect";
import {
  buildPendingUserInputAnswers,
  derivePendingUserInputProgress,
  type PendingUserInputDraftAnswer,
  type PendingUserInputProgress,
} from "../pendingUserInput";
import {
  type ChatMessage,
  type Project,
  type SessionPhase,
  type Thread,
  type ThreadSession,
} from "../types";
import type { DraftThreadState } from "../composerDraftStore";
import { Schema } from "effect";
import { selectThreadByRef, useStore } from "../store";
import { getProviderModelCapabilities } from "../providerModels";
import type {
  GitHubActionRequiredRequest,
  GitHubToolConfirmationRequest,
  PendingUserInput,
} from "../session-logic";
import { shortcutLabelForCommand } from "../keybindings";
import { deriveLogicalProjectKey } from "../logicalProject";
import { resolveEnvironmentOptionLabel } from "./BranchToolbar.logic";
import { threadHasStarted } from "./ChatView.composer";
import { resolveServerImagePreviewUrlsForHandoff } from "./ChatView.timeline";
import { resolveEnvironmentHttpUrl } from "../environments/runtime/catalog";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary";
import { buildPrimaryFetchInit } from "../environments/primary/fetchInit";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "forge:last-invoked-script-by-project";
export const MAX_HIDDEN_MOUNTED_TERMINAL_THREADS = 10;
const GITHUB_TOOL_CONFIRMATION_APPROVE_PATH =
  "/.well-known/forge/github/tool-confirmations/approve";

/**
 * Wire contract: the server returns this stable code (with a human-readable
 * `error`) when an approval targets a confirmation that no longer exists, was
 * already used, or belongs to a different/replaced provider session. Kept in
 * sync with `GITHUB_CONFIRMATION_INVALID_CODE` in GitHubAgentToolRoutes.ts.
 */
const GITHUB_CONFIRMATION_INVALID_CODE = "github_confirmation_invalid";

/** Legacy fallback for runtimes predating the machine-readable code, matching a
 * stable substring so a mixed-version fleet still shows expired, not retryable. */
const GITHUB_CONFIRMATION_INVALID_LEGACY_PATTERN = /belongs to another session/i;

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export {
  createThreadPlanCatalogSelector,
  type ThreadPlanCatalogEntry,
} from "./ChatView.threadPlanCatalog";
export {
  buildTimelineMessagesWithPreviewHandoffs,
  deriveRevertTurnCountByUserMessageId,
  resolveServerImagePreviewUrlsForHandoff,
} from "./ChatView.timeline";
export {
  buildExpiredTerminalContextToastCopy,
  cloneComposerImageForRetry,
  deriveComposerSendPreflight,
  deriveComposerSendState,
  deriveThreadEnvModeState,
  threadHasStarted,
} from "./ChatView.composer";
export {
  buildProjectScriptTerminalLaunchPlan,
  deriveActiveTaskContextModel,
  resolveActivePersistentTerminalLaunchContext,
  resolvePersistentTerminalCwd,
  resolvePersistentTerminalProjectRef,
  resolvePersistentTerminalRuntimeEnv,
  resolvePersistentTerminalWorktreePath,
  SCRIPT_TERMINAL_COLS,
  SCRIPT_TERMINAL_ROWS,
} from "./ChatView.terminal";
export { deriveDraftThreadProviderState } from "./chat/draftThreadProviderState";

const DESIGN_KEYWORDS =
  /\b(design|poster|banner|flyer|post|social\s*media|canvas|slide|presentation|infographic|thumbnail|cover|ad|advertisement|brochure|card|invitation|logo|layout|graphic)\b/i;

export function isDesignRelatedPrompt(text: string): boolean {
  return DESIGN_KEYWORDS.test(text);
}

export function buildGitHubConfirmationRetryPrompt(
  confirmation: GitHubToolConfirmationRequest,
): string {
  return [
    "The GitHub write action was approved by the user.",
    `Retry the dynamic tool \`${confirmation.toolName}\` with this approved confirmationId: \`${confirmation.confirmationId}\`.`,
    "Use the same request payload, plus the confirmationId field:",
    "```json",
    JSON.stringify(
      { ...confirmation.request, confirmationId: confirmation.confirmationId },
      null,
      2,
    ),
    "```",
  ].join("\n");
}

export function buildGitHubConfirmationExecutedPrompt(
  confirmation: GitHubToolConfirmationRequest,
): string {
  return [
    "The GitHub write action was approved and executed by the user.",
    `The \`${confirmation.toolName}\` action completed successfully. Do not retry it.`,
    "Continue with your original task.",
  ].join("\n");
}

export function buildGitHubActionRequiredRetryPrompt(
  actionRequired: GitHubActionRequiredRequest,
): string {
  const toolClause = actionRequired.toolName
    ? `Retry the previous GitHub tool request for \`${actionRequired.toolName}\`.`
    : "Retry the previous GitHub tool request.";
  return [
    "The required Forge GitHub setup has been completed by the user.",
    toolClause,
    "Continue the previous GitHub lifecycle request using Forge brokered GitHub tools only.",
    "Do not use shell `gh` or direct GitHub CLI commands.",
  ].join("\n");
}

export function formatOutgoingPrompt(params: {
  provider: ProviderKind;
  model: string | null;
  models: ReadonlyArray<ServerProvider["models"][number]>;
  effort: string | null;
  text: string;
}): string {
  const caps = getProviderModelCapabilities(params.models, params.model, params.provider);
  if (params.effort && caps.promptInjectedEffortLevels.includes(params.effort)) {
    return applyClaudePromptEffortPrefix(params.text, params.effort as ClaudeCodeEffort | null);
  }
  return params.text;
}

export function hasLaunchRuntimeEnv(context: {
  runtimeEnv?: Record<string, string> | undefined;
}): boolean {
  return Boolean(context.runtimeEnv && Object.keys(context.runtimeEnv).length > 0);
}

export interface LogicalProjectEnvironmentOption {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly label: string;
  readonly isPrimary: boolean;
}

export function deriveLogicalProjectEnvironmentOptions(input: {
  readonly activeProject: Project | null | undefined;
  readonly allProjects: ReadonlyArray<Project>;
  readonly primaryEnvironmentId: EnvironmentId | null | undefined;
  readonly savedEnvironmentRegistry: Readonly<
    Record<string, { readonly label?: string | null } | undefined>
  >;
  readonly savedEnvironmentRuntimeById: Readonly<
    Record<string, { readonly descriptor?: { readonly label?: string | null } | null } | undefined>
  >;
}): LogicalProjectEnvironmentOption[] {
  if (!input.activeProject) {
    return [];
  }

  const logicalKey = deriveLogicalProjectKey(input.activeProject);
  const seen = new Set<string>();
  const environments: LogicalProjectEnvironmentOption[] = [];

  for (const project of input.allProjects) {
    if (deriveLogicalProjectKey(project) !== logicalKey || seen.has(project.environmentId)) {
      continue;
    }

    seen.add(project.environmentId);
    const isPrimary = project.environmentId === input.primaryEnvironmentId;
    const savedRecord = input.savedEnvironmentRegistry[project.environmentId];
    const runtimeState = input.savedEnvironmentRuntimeById[project.environmentId];
    environments.push({
      environmentId: project.environmentId,
      projectId: project.id,
      label: resolveEnvironmentOptionLabel({
        isPrimary,
        environmentId: project.environmentId,
        runtimeLabel: runtimeState?.descriptor?.label ?? null,
        savedLabel: savedRecord?.label ?? null,
      }),
      isPrimary,
    });
  }

  environments.sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) {
      return a.isPrimary ? -1 : 1;
    }
    return a.label.localeCompare(b.label);
  });
  return environments;
}

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
  error: string | null,
): Thread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    codexThreadId: null,
    projectId: draftThread.projectId,
    title: "New thread",
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: draftThread.interactionMode,
    session: null,
    messages: [],
    error,
    createdAt: draftThread.createdAt,
    archivedAt: null,
    settledAt: null,
    settledOverride: null,
    latestTurn: null,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    turnDiffSummaries: [],
    activities: [],
    proposedPlans: [],
    anchorKind: "scratch",
    anchorCmmdId: null,
    kind: "master",
    parentThreadId: null,
    status: "idle",
    workerRole: null,
    goal: null,
  };
}

const EMPTY_PENDING_USER_INPUT_ANSWERS: Record<string, PendingUserInputDraftAnswer> = {};

function cloneAttachmentPreviewHandoffs(
  handoffsByMessageId: Readonly<Record<string, ReadonlyArray<string>>>,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(handoffsByMessageId).map(([messageId, previewUrls]) => [
      messageId,
      [...previewUrls],
    ]),
  );
}

export function removeAttachmentPreviewHandoff(input: {
  readonly handoffsByMessageId: Readonly<Record<string, ReadonlyArray<string>>>;
  readonly messageId: MessageId;
  readonly previewUrls?: ReadonlyArray<string> | undefined;
}): {
  readonly nextHandoffsByMessageId: Record<string, string[]>;
  readonly previewUrlsToRevoke: ReadonlyArray<string>;
  readonly changed: boolean;
} {
  const existingPreviewUrls = input.handoffsByMessageId[input.messageId];
  const previewUrlsToRevoke = input.previewUrls ?? existingPreviewUrls ?? [];
  if (!(input.messageId in input.handoffsByMessageId)) {
    return {
      nextHandoffsByMessageId: cloneAttachmentPreviewHandoffs(input.handoffsByMessageId),
      previewUrlsToRevoke,
      changed: false,
    };
  }
  const nextHandoffsByMessageId = cloneAttachmentPreviewHandoffs(input.handoffsByMessageId);
  delete nextHandoffsByMessageId[input.messageId];
  return {
    nextHandoffsByMessageId,
    previewUrlsToRevoke,
    changed: true,
  };
}

export function upsertAttachmentPreviewHandoff(input: {
  readonly handoffsByMessageId: Readonly<Record<string, ReadonlyArray<string>>>;
  readonly messageId: MessageId;
  readonly previewUrls: ReadonlyArray<string>;
}): {
  readonly nextHandoffsByMessageId: Record<string, string[]>;
  readonly previewUrlsToRevoke: ReadonlyArray<string>;
  readonly changed: boolean;
} {
  if (input.previewUrls.length === 0) {
    return {
      nextHandoffsByMessageId: cloneAttachmentPreviewHandoffs(input.handoffsByMessageId),
      previewUrlsToRevoke: [],
      changed: false,
    };
  }
  const previousPreviewUrls = input.handoffsByMessageId[input.messageId] ?? [];
  const nextPreviewUrls = [...input.previewUrls];
  const nextHandoffsByMessageId = cloneAttachmentPreviewHandoffs(input.handoffsByMessageId);
  nextHandoffsByMessageId[input.messageId] = nextPreviewUrls;
  return {
    nextHandoffsByMessageId,
    previewUrlsToRevoke: previousPreviewUrls.filter(
      (previewUrl) => !input.previewUrls.includes(previewUrl),
    ),
    changed: true,
  };
}

export function deriveAttachmentPreviewPromotionCandidates(input: {
  readonly serverMessages: ReadonlyArray<ChatMessage> | null | undefined;
  readonly handoffsByMessageId: Readonly<Record<string, ReadonlyArray<string>>>;
  readonly promotionInFlightByMessageId: Readonly<Record<string, true | undefined>>;
}): Array<{
  readonly messageId: MessageId;
  readonly handoffPreviewUrls: ReadonlyArray<string>;
  readonly serverPreviewUrls: ReadonlyArray<string>;
}> {
  if (!input.serverMessages || input.serverMessages.length === 0) {
    return [];
  }

  return Object.entries(input.handoffsByMessageId).flatMap(([messageId, handoffPreviewUrls]) => {
    if (input.promotionInFlightByMessageId[messageId]) {
      return [];
    }

    const serverPreviewUrls = resolveServerImagePreviewUrlsForHandoff({
      serverMessages: input.serverMessages,
      messageId,
      handoffPreviewUrls,
    });
    return serverPreviewUrls
      ? [
          {
            messageId: messageId as MessageId,
            handoffPreviewUrls,
            serverPreviewUrls,
          },
        ]
      : [];
  });
}

export function deriveActivePendingUserInputState(input: {
  readonly pendingUserInputs: ReadonlyArray<PendingUserInput>;
  readonly draftAnswersByRequestId: Readonly<
    Record<string, Record<string, PendingUserInputDraftAnswer> | undefined>
  >;
  readonly questionIndexByRequestId: Readonly<Record<string, number | undefined>>;
  readonly respondingRequestIds: ReadonlyArray<string>;
}): {
  readonly activePendingUserInput: PendingUserInput | null;
  readonly activePendingDraftAnswers: Record<string, PendingUserInputDraftAnswer>;
  readonly activePendingQuestionIndex: number;
  readonly activePendingProgress: PendingUserInputProgress | null;
  readonly activePendingResolvedAnswers: Record<string, string | string[]> | null;
  readonly activePendingIsResponding: boolean;
} {
  const activePendingUserInput = input.pendingUserInputs[0] ?? null;
  if (!activePendingUserInput) {
    return {
      activePendingUserInput: null,
      activePendingDraftAnswers: EMPTY_PENDING_USER_INPUT_ANSWERS,
      activePendingQuestionIndex: 0,
      activePendingProgress: null,
      activePendingResolvedAnswers: null,
      activePendingIsResponding: false,
    };
  }

  const activePendingDraftAnswers =
    input.draftAnswersByRequestId[activePendingUserInput.requestId] ??
    EMPTY_PENDING_USER_INPUT_ANSWERS;
  const activePendingQuestionIndex =
    input.questionIndexByRequestId[activePendingUserInput.requestId] ?? 0;

  return {
    activePendingUserInput,
    activePendingDraftAnswers,
    activePendingQuestionIndex,
    activePendingProgress: derivePendingUserInputProgress(
      activePendingUserInput.questions,
      activePendingDraftAnswers,
      activePendingQuestionIndex,
    ),
    activePendingResolvedAnswers: buildPendingUserInputAnswers(
      activePendingUserInput.questions,
      activePendingDraftAnswers,
    ),
    activePendingIsResponding: input.respondingRequestIds.includes(
      activePendingUserInput.requestId,
    ),
  };
}

export function shouldWriteThreadErrorToCurrentServerThread(input: {
  serverThread:
    | {
        environmentId: EnvironmentId;
        id: ThreadId;
      }
    | null
    | undefined;
  routeThreadRef: ScopedThreadRef;
  targetThreadId: ThreadId;
}): boolean {
  return Boolean(
    input.serverThread &&
    input.targetThreadId === input.routeThreadRef.threadId &&
    input.serverThread.environmentId === input.routeThreadRef.environmentId &&
    input.serverThread.id === input.targetThreadId,
  );
}

export function reconcileMountedTerminalThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadTerminalOpen: boolean;
  maxHiddenThreadCount?: number;
}): string[] {
  const openThreadIdSet = new Set(input.openThreadIds);
  const hiddenThreadIds = input.currentThreadIds.filter(
    (threadId) => threadId !== input.activeThreadId && openThreadIdSet.has(threadId),
  );
  const maxHiddenThreadCount = Math.max(
    0,
    input.maxHiddenThreadCount ?? MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  );
  // Guard against `maxHiddenThreadCount === 0`: `slice(-0)` is `slice(0)`, which would
  // keep the entire hidden set instead of dropping it. Take the newest N explicitly.
  const nextThreadIds =
    hiddenThreadIds.length > maxHiddenThreadCount
      ? hiddenThreadIds.slice(hiddenThreadIds.length - maxHiddenThreadCount)
      : hiddenThreadIds;

  if (
    input.activeThreadId &&
    input.activeThreadTerminalOpen &&
    !nextThreadIds.includes(input.activeThreadId)
  ) {
    nextThreadIds.push(input.activeThreadId);
  }

  return nextThreadIds;
}

export function deriveMountedTerminalThreadIds(input: {
  readonly activeThreadKey: string | null;
  readonly activeThreadTerminalOpen: boolean;
  readonly currentThreadIds: ReadonlyArray<string>;
  readonly draftThreadKeys: ReadonlyArray<string>;
  readonly openTerminalThreadKeys: ReadonlyArray<string>;
  readonly serverThreadKeys: ReadonlyArray<string>;
  readonly maxHiddenThreadCount?: number | undefined;
}): string[] {
  const existingThreadKeys = new Set([...input.serverThreadKeys, ...input.draftThreadKeys]);
  const openThreadIds = input.openTerminalThreadKeys.filter((threadKey) =>
    existingThreadKeys.has(threadKey),
  );
  return reconcileMountedTerminalThreadIds({
    currentThreadIds: input.currentThreadIds,
    openThreadIds,
    activeThreadId: input.activeThreadKey,
    activeThreadTerminalOpen: input.activeThreadTerminalOpen,
    ...(input.maxHiddenThreadCount !== undefined
      ? { maxHiddenThreadCount: input.maxHiddenThreadCount }
      : {}),
  });
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read image data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read image."));
    });
    reader.readAsDataURL(file);
  });
}

interface GitHubToolConfirmationApprovalRequest {
  readonly confirmationId: string;
  readonly toolName: string;
  readonly request: unknown;
}

/**
 * Error thrown when the approval endpoint rejects a request. `expired` is true
 * when the rejection means the confirmation is permanently un-approvable (gone,
 * already used, or issued by a now-replaced provider session) — the card should
 * lock into a terminal "expired" state instead of re-enabling Approve. Other
 * rejections (install required, transient failures) leave `expired` false so the
 * card stays retryable.
 */
class GitHubConfirmationApprovalError extends Error {
  readonly expired: boolean;
  readonly code?: string;

  constructor(message: string, options: { readonly expired: boolean; readonly code?: string }) {
    super(message);
    this.name = "GitHubConfirmationApprovalError";
    this.expired = options.expired;
    if (options.code !== undefined) {
      this.code = options.code;
    }
  }
}

/**
 * Returns true when an unknown caught value is an approval error that means the
 * underlying confirmation is no longer actionable (expired/replaced session).
 * Duck-typed so it works across bundles and tolerates serialization.
 */
export function isExpiredGitHubConfirmationApprovalError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "expired" in error &&
    (error as { readonly expired?: unknown }).expired === true
  );
}

export async function approveGitHubToolConfirmationRequest(input: {
  readonly confirmation: GitHubToolConfirmationApprovalRequest;
  readonly threadId: ThreadId;
  readonly resolveUrl: (pathname: string) => string;
  readonly buildFetchInit: (base: RequestInit) => RequestInit;
  readonly fetchImpl?: typeof fetch;
}): Promise<{
  confirmationId?: unknown;
  error?: unknown;
  execution?: {
    ok?: unknown;
    executed?: unknown;
    error?: unknown;
    code?: unknown;
    userActionRequired?: unknown;
  } | null;
} | null> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const response = await fetchImpl(
    input.resolveUrl(GITHUB_TOOL_CONFIRMATION_APPROVE_PATH),
    input.buildFetchInit({
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        confirmationId: input.confirmation.confirmationId,
        threadId: input.threadId,
        toolName: input.confirmation.toolName,
        request: input.confirmation.request,
      }),
    }),
  );
  const body = (await response.json().catch(() => null)) as {
    confirmationId?: unknown;
    error?: unknown;
    execution?: {
      ok?: unknown;
      executed?: unknown;
      error?: unknown;
      code?: unknown;
      userActionRequired?: unknown;
    } | null;
  } | null;
  if (!response.ok) {
    const message = typeof body?.error === "string" ? body.error : "Approval failed.";
    const code =
      typeof (body as { code?: unknown })?.code === "string"
        ? (body as { code: string }).code
        : undefined;
    const expired =
      response.status === 404 &&
      (code === GITHUB_CONFIRMATION_INVALID_CODE ||
        GITHUB_CONFIRMATION_INVALID_LEGACY_PATTERN.test(message));
    throw new GitHubConfirmationApprovalError(message, {
      expired,
      ...(code !== undefined ? { code } : {}),
    });
  }
  return body;
}

/**
 * Resolve WHERE a GitHub tool-confirmation approval POST must go for a thread.
 *
 * A cloud thread's confirmation row lives in the USER-VM (the codex adapter
 * that creates it runs there), reachable only via the runtime router; a local
 * thread's lives on the master. Routing a cloud approval through the master
 * 404s (`github_confirmation_invalid`, no such row), and the SPA shows a
 * misleading "approval expired / session replaced" card while the agent loops
 * re-creating confirmations forever. Target the thread's own environment +
 * bearer instead, the same path the github clone uses. credentials:"omit"
 * because the runtime router is a cross-origin Fly URL with no shared cookie.
 */
export function buildGitHubConfirmationRequestContext(input: {
  readonly environmentId: EnvironmentId;
  readonly bearerToken: string | null;
  readonly deps?: {
    readonly resolveEnvironmentUrl?: (args: {
      readonly environmentId: EnvironmentId;
      readonly pathname: string;
    }) => string;
    readonly resolvePrimaryUrl?: (pathname: string) => string;
    readonly buildPrimaryInit?: (base: RequestInit) => RequestInit;
  };
}): {
  readonly resolveUrl: (pathname: string) => string;
  readonly buildFetchInit: (base: RequestInit) => RequestInit;
} {
  const resolveEnvUrl = input.deps?.resolveEnvironmentUrl ?? resolveEnvironmentHttpUrl;
  const resolvePrimaryUrl = input.deps?.resolvePrimaryUrl ?? resolvePrimaryEnvironmentHttpUrl;
  const buildPrimaryInit = input.deps?.buildPrimaryInit ?? buildPrimaryFetchInit;

  if (classifyEnvironmentId(input.environmentId).isCloud) {
    const bearer = input.bearerToken;
    return {
      resolveUrl: (pathname) => resolveEnvUrl({ environmentId: input.environmentId, pathname }),
      buildFetchInit: (base) => {
        const headers = new Headers(base.headers);
        if (bearer && !headers.has("authorization")) {
          headers.set("authorization", `Bearer ${bearer}`);
        }
        return { ...base, credentials: "omit", headers };
      },
    };
  }

  return { resolveUrl: resolvePrimaryUrl, buildFetchInit: buildPrimaryInit };
}
export function buildGitHubConfirmationFailedPrompt(
  confirmation: GitHubToolConfirmationApprovalRequest,
  error: string,
): string {
  return [
    "The GitHub write action was approved but failed to execute.",
    `The \`${confirmation.toolName}\` action failed with the following error:`,
    error,
    "Inform the user of the failure. They may need to fix the underlying issue (e.g. check permissions, resolve conflicts) before retrying.",
  ].join("\n");
}

const CMMD_REAUTH_REQUIRED_CODE = "CMMD_REAUTH_REQUIRED";
const CONNECT_CMMD_ACTION = "connect_cmmd";

/**
 * The brokered GitHub tools mint their GitHub token from the runtime's CMMD grant.
 * `userActionRequired: "connect_cmmd"`); for an approve→execute failure those
 * same markers ride back on `body.execution`. Detect either so the SPA can
 * surface an actionable re-auth CTA instead of relaying dead-end text.
 */
export function executionRequiresCmmdReauth(
  execution: { code?: unknown; userActionRequired?: unknown } | null | undefined,
): boolean {
  if (!execution) return false;
  return (
    execution.code === CMMD_REAUTH_REQUIRED_CODE ||
    execution.userActionRequired === CONNECT_CMMD_ACTION
  );
}

/**
 * Where the user re-authenticates an expired CMMD session — the primary/master
 * Forge login (mirrors lib/authExpiredRedirect.ts). NOTE for cloud threads: this
 * refreshes only the Cloud Shell session; the runtime refreshes its own grant on
 * the next runtime reconnect/turn, so the CTA copy must tell the user to
 * reconnect + retry after signing in.
 */
export function buildCmmdReauthHref(
  resolvePrimaryUrl: (pathname: string) => string = resolvePrimaryEnvironmentHttpUrl,
): string {
  // Seed the expiry reason as a sessionStorage one-shot (consistent with
  // lib/authExpiredRedirect.ts) instead of a sticky ?reason= URL param, which
  // the login surface no longer reads and which would persist across reloads.
  // The login page consumes it once via takeAuthExpiredReason().
  try {
    window.sessionStorage.setItem(AUTH_EXPIRED_REASON_KEY, "session-expired");
  } catch {
    // sessionStorage unavailable — the CTA still navigates to a clean /login.
  }
  return resolvePrimaryUrl("/login");
}

/**
 * Thrown from the approve flow when an approve→execute failure is a CMMD
 * re-auth requirement, so the confirmation card can render the re-auth CTA
 * instead of relaying a generic failure prompt to the agent.
 */
export class GitHubReauthRequiredError extends Error {
  readonly reauthHref: string;
  constructor(message: string, reauthHref: string) {
    super(message);
    this.name = "GitHubReauthRequiredError";
    this.reauthHref = reauthHref;
  }
}

// Duck-type on the error name + reauthHref (rather than instanceof or a
// dedicated discriminant field) so it survives HMR/cross-bundle identity and
// avoids an unused-class-member; reauthHref is read on the narrowed instance.
export function isGitHubReauthRequiredError(error: unknown): error is GitHubReauthRequiredError {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "GitHubReauthRequiredError" &&
    typeof (error as { reauthHref?: unknown }).reauthHref === "string"
  );
}

/**
 * Thrown from the approve flow when the server reports the brokered GitHub action
 * was approved but FAILED to execute (`execution.executed === false`) for a
 * non-reauth reason — e.g. permissions, rate limit, merge conflict, network. The
 * agent still receives a failure-context follow-up prompt, but the approval card
 * (and the co-equal overlay) must surface a "failed" state to the user rather
 * than a misleading green "Approved" confirmation. Both surfaces route any
 * non-expired / non-reauth throw to their "failed" UI, so throwing this is
 * sufficient to flip them.
 */
export class GitHubConfirmationExecutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GitHubConfirmationExecutionError";
  }
}
export function buildGitHubConfirmationDeclinedPrompt(
  confirmation: GitHubToolConfirmationRequest,
): string {
  return [
    "The user declined the GitHub write action.",
    `The \`${confirmation.toolName}\` action was not executed.`,
    "Ask the user what they would like to do instead.",
  ].join("\n");
}

export function shouldShowTeamPanel(
  threadKind: ThreadKind | null | undefined,
  workerCount: number,
): boolean {
  return threadKind === "master" && workerCount > 0;
}

export async function waitForStartedServerThread(
  threadRef: ScopedThreadRef,
  timeoutMs = 1_000,
): Promise<boolean> {
  const getThread = () => selectThreadByRef(useStore.getState(), threadRef);
  const thread = getThread();

  if (threadHasStarted(thread)) {
    return true;
  }

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (result: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      unsubscribe();
      resolve(result);
    };

    const unsubscribe = useStore.subscribe((state) => {
      if (!threadHasStarted(selectThreadByRef(state, threadRef))) {
        return;
      }
      finish(true);
    });

    if (threadHasStarted(getThread())) {
      finish(true);
      return;
    }

    timeoutId = globalThis.setTimeout(() => {
      finish(false);
    }, timeoutMs);
  });
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionOrchestrationStatus: ThreadSession["orchestrationStatus"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionOrchestrationStatus: session?.orchestrationStatus ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

export function deriveNextLocalDispatchSnapshot(input: {
  current: LocalDispatchSnapshot | null;
  activeThread: Thread | undefined;
  options?: { preparingWorktree?: boolean } | undefined;
}): LocalDispatchSnapshot {
  const preparingWorktree = Boolean(input.options?.preparingWorktree);
  if (input.current) {
    return input.current.preparingWorktree === preparingWorktree
      ? input.current
      : { ...input.current, preparingWorktree };
  }
  return createLocalDispatchSnapshot(input.activeThread, input.options);
}

export function deriveLocalDraftError(input: {
  readonly routeKind: "server" | "draft";
  readonly serverThread: Thread | undefined;
  readonly draftId: string | null;
  readonly localDraftErrorsByDraftId: Readonly<Record<string, string | null | undefined>>;
}): string | null {
  if (input.routeKind === "server" && input.serverThread) {
    return null;
  }
  if (!input.draftId) {
    return null;
  }
  return input.localDraftErrorsByDraftId[input.draftId] ?? null;
}

export function deriveServerThreadStatus(input: {
  readonly routeKind: "server" | "draft";
  readonly serverThread: Thread | undefined;
  readonly subscriptionError: unknown;
}): "ready" | "loading" | "missing" {
  if (input.routeKind !== "server") {
    return "ready";
  }
  if (input.serverThread !== undefined) {
    return "ready";
  }
  return input.subscriptionError !== undefined ? "missing" : "loading";
}

export function deriveChatViewRouteState(input: {
  readonly draftThread: Thread | undefined;
  readonly routeKind: "server" | "draft";
  readonly serverThread: Thread | undefined;
}): {
  readonly activeThread: Thread | undefined;
  readonly isLocalDraftThread: boolean;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
} {
  const isServerThread = input.routeKind === "server" && input.serverThread !== undefined;
  const isLocalDraftThread = !isServerThread && input.draftThread !== undefined;
  return {
    activeThread: isServerThread ? input.serverThread : input.draftThread,
    isLocalDraftThread,
    isServerThread,
    canOpenRightSidebar: isServerThread || isLocalDraftThread,
  };
}

export function derivePersistentTerminalDrawerViewModel(input: {
  readonly hasProject: boolean;
  readonly terminalOpen: boolean;
  readonly hasCwd: boolean;
  readonly visible: boolean;
  readonly parentFocusRequestId: number;
  readonly localFocusRequestId: number;
  readonly splitShortcutLabel: string | undefined;
  readonly newShortcutLabel: string | undefined;
  readonly closeShortcutLabel: string | undefined;
}):
  | {
      readonly shouldRender: false;
    }
  | {
      readonly shouldRender: true;
      readonly wrapperClassName: string | undefined;
      readonly focusRequestId: number;
      readonly splitShortcutLabel: string | undefined;
      readonly newShortcutLabel: string | undefined;
      readonly closeShortcutLabel: string | undefined;
    } {
  if (!input.hasProject || !input.terminalOpen || !input.hasCwd) {
    return { shouldRender: false };
  }
  return {
    shouldRender: true,
    wrapperClassName: input.visible ? undefined : "hidden",
    focusRequestId:
      input.parentFocusRequestId + input.localFocusRequestId + (input.visible ? 1 : 0),
    splitShortcutLabel: input.visible ? input.splitShortcutLabel : undefined,
    newShortcutLabel: input.visible ? input.newShortcutLabel : undefined,
    closeShortcutLabel: input.visible ? input.closeShortcutLabel : undefined,
  };
}

// This inherited dispatch state machine is only shifted by provider-state additions above.
// fallow-ignore-next-line complexity
export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (input.hasPendingApproval || input.hasPendingUserInput || Boolean(input.threadError)) {
    return true;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (input.phase === "running") {
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== undefined &&
      session.activeTurnId !== null &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    input.localDispatch.sessionOrchestrationStatus !== (session?.orchestrationStatus ?? null) ||
    input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}

export interface ChatViewShortcutLabels {
  readonly terminalToggleShortcutLabel: string | null;
  readonly splitTerminalShortcutLabel: string | null;
  readonly newTerminalShortcutLabel: string | null;
  readonly closeTerminalShortcutLabel: string | null;
  readonly diffPanelShortcutLabel: string | null;
  readonly canvasPanelShortcutLabel: string | null;
  readonly previewPanelShortcutLabel: string | null;
  readonly planPanelShortcutLabel: string | null;
  readonly commandPaletteShortcutLabel: string | null;
  readonly jumpToParentShortcutLabel: string | null;
}

export function deriveChatViewShortcutLabels(input: {
  readonly keybindings: ResolvedKeybindingsConfig;
  readonly terminalOpen: boolean;
}): ChatViewShortcutLabels {
  const terminalShortcutLabelOptions = {
    context: {
      terminalFocus: true,
      terminalOpen: input.terminalOpen,
    },
  };
  const nonTerminalShortcutLabelOptions = {
    context: {
      terminalFocus: false,
      terminalOpen: input.terminalOpen,
    },
  };

  return {
    terminalToggleShortcutLabel: shortcutLabelForCommand(input.keybindings, "terminal.toggle"),
    splitTerminalShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "terminal.split",
      terminalShortcutLabelOptions,
    ),
    newTerminalShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "terminal.new",
      terminalShortcutLabelOptions,
    ),
    closeTerminalShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "terminal.close",
      terminalShortcutLabelOptions,
    ),
    diffPanelShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "diff.toggle",
      nonTerminalShortcutLabelOptions,
    ),
    canvasPanelShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "canvas.toggle",
      nonTerminalShortcutLabelOptions,
    ),
    previewPanelShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "preview.toggle",
      nonTerminalShortcutLabelOptions,
    ),
    planPanelShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "plan.toggle",
      nonTerminalShortcutLabelOptions,
    ),
    commandPaletteShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "commandPalette.toggle",
      nonTerminalShortcutLabelOptions,
    ),
    jumpToParentShortcutLabel: shortcutLabelForCommand(
      input.keybindings,
      "thread.jumpToParent",
      nonTerminalShortcutLabelOptions,
    ),
  };
}
